<template>
  <draggable
    v-model="items"
    :group="{ name: 'shoppable', pull: true, put: ['shoppable'] }"
    class="tgbDropArea tgbDropArea--shoppable"
    :class="{ 'empty': !items.length }"
    data-test="producer-shoppable-droparea"
    @change="addToShoppable"
  >
    <p v-if="!items.length && !addingShoppableItem" class="tgbDropArea--shoppable__help">Drag shoppable here</p>

    <div
      v-else
      v-for="(p, index) in items"
      :key="index"
      class="tgbDropArea--shoppable__item"
    >
      <div class="tgbDropArea--shoppable__media relative">
        <div v-if="addingShoppableItem" class="flex items-center content-center h-full">
          <span
            class="fas fa-spinner fa-spin"
          />
        </div>

        <img
          v-else
          class="tgbDropArea--shoppable__image h-full max-h-full w-full max-w-full object-contain"
          :src="p.image"
          :alt="p.name"
        >
      </div>

      <div
        v-if="items.length"
        class="ml-1 flex h-full flex-col items-center justify-between"
      >
        <span class="shoppable-dropzone-store-icon" :class="storeIcon"></span>

        <button
          class="tb-btn tb-btn-link xs text-danger text-[13px]"
          @click="$emit('remove', p)"
        >
          <span class="far fa-trash-alt"></span>
        </button>
      </div>
    </div>
  </draggable>
</template>

<script>
import { mapState } from 'vuex';
import { getStoreIcon } from '@/js/utils/shoppable';
import { ensureTrackingLinkMetadataRid, pickTrackingFields } from '@/js/utils/tracking-links';
import { ensureShoppableTrackingLink } from '@/js/services/tracking-links';
import { encodeAffiliateMeta } from '../../../utils/affiliate';
import { getEnvironment } from '../../../utils/config';

/**
 * @typedef {Object} FanaticsProduct
 * @property {string} Id
 * @property {string} CatalogId
 * @property {string} CampaignId
 * @property {string} CampaignName
 * @property {string} CatalogItemId
 * @property {string} Name
 * @property {string} Description
 * @property {string} Manufacturer
 * @property {string} Url
 * @property {string} ImageUrl
 * @property {string} Currency
 * @property {string} CurrentPrice
 */

/**
 * @typedef {FanaticsProduct} AffiliateProduct
 */

export default {
  name: 'ShoppableDropzone',

  props: {
    modelValue: {
      type: Array,
      default: () => [],
      required: true,
    },
    graphic: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    element: {
      type: Object,
      default: () => ({}),
      required: true,
    },
  },

  data() {
    return {
      addingShoppableItem: false,
    };
  },

  computed: {
    ...mapState({
      production: (store) => store.production.production,
    }),

    ...mapState('shoppable', [
      'amazonTrackingId',
    ]),

    items: {
      get() {
        return this.modelValue ?? [];
      },

      set() {
        // draggable will set this, but we handle updates via @change event
        // so we don't need to emit here
      },
    },

    storeIcon() {
      const { type } = this.modelValue[0];
      return getStoreIcon(type);
    },
  },
  methods: {
    async addToShoppable({ added, removed }) {
      if (removed) {
        this.$emit('remove', removed);
        return;
      }

      // Set shoppable element fields. Clone so we don't mutate the catalog/store
      // product (each drop is its own occurrence with its own metadata rid).
      // A product may already carry tracking fields from `meta` (flattened or
      // nested); normalize both. Otherwise the link is upserted below the first
      // time the product is used.
      const sourceProduct = added.element?.rawData || added.element;
      const selectedProduct = {
        ...sourceProduct,
        ...pickTrackingFields(sourceProduct),
      };

      this.addingShoppableItem = true;
      const affiliateUrl = this.makeAffiliate(sourceProduct);
      if (!affiliateUrl) {
        this.addingShoppableItem = false;
        return;
      }

      // Upsert the static `shoppable` TrackingLink the first time this saved
      // product is used. It is reused for every later occurrence, so links are
      // never created for products that are saved but unused.
      if (!selectedProduct.trackingLinkRid && sourceProduct.id) {
        try {
          const fields = await ensureShoppableTrackingLink(sourceProduct.id);
          Object.assign(selectedProduct, fields);
        } catch (err) {
          console.error('Failed to ensure shoppable tracking link:', err);
        }
      }

      // The affiliate URL is kept for display/legacy fallback. The QR code now
      // renders from the static TrackingLink created the first time the product
      // is used, so we only need to make sure the occurrence has a metadata rid.
      selectedProduct.affiliateUrl = affiliateUrl;
      ensureTrackingLinkMetadataRid(selectedProduct);

      this.$emit('input', selectedProduct);
      this.$emit('update:modelValue', selectedProduct);

      this.addingShoppableItem = false;
    },

    makeAffiliate(product) {
      const {
        source,
        sourceUrl,
      } = product;

      switch (source) {
        case 'fanatics': {
          return this.getFanaticsLink(product);
        }

        case 'amazon': {
          if (this.amazonTrackingId) {
            const newUrl = new URL(sourceUrl);
            newUrl.searchParams.set('tag', this.amazonTrackingId);
            return newUrl.href;
          }

          return sourceUrl;
        }

        default: {
          return sourceUrl;
        }
      }
    },

    /**
     * @param {string} url
     * @returns {string}
     */
    getFanaticsLink(product) {
      if (!product) { return null; }

      const { sourceUrl } = product;
      const newUrl = new URL(sourceUrl);

      // NOTE: The encoded string of this object can't
      // exceed 255 characters for Fanatics/Impact, so we need to be careful
      // of what values we add here.
      const affiliateLinkData = {
        affiliate: 'fanatics',
        ownerId: this.production.owner,
        productionId: this.production.id,
        graphicTemplateId: this.graphic.template_id,
        catalogId: product?.CatalogId,
        campaignId: product?.CampaignId,
        sku: product?.CatalogItemId,
        timestamp: new Date().getTime(),
        env: getEnvironment(),
      };

      const subId3 = encodeAffiliateMeta(affiliateLinkData);

      newUrl.searchParams.append('subId1', this.production.owner);
      newUrl.searchParams.append('subId2', this.production.id);
      newUrl.searchParams.append('subId3', subId3);

      return newUrl.href;
    },
  },
};
</script>

<style lang="scss">
.shoppable-dropzone-store-icon {
  font-size: 0.875rem;
  padding-top: 0.25rem;
}
</style>
